// import React from "react";

import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";


const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 }, // FIXED duplicate opacity
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 }, // FIXED duplicate opacity
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};




export default function MinivelWhy() {
  const cards = [
    {
      title: "HR & IT Delivery at Scale",
      text: "High-volume hiring, project-based staffing, and HR operations designed for enterprise workloads and strict SLAs.",
    },
    {
      title: "Process-Driven Governance",
      text: "Standardized workflows, SLAs, and review mechanisms that mirror large IT & shared services environments.",
    },
    {
      title: "Integrated Workforce View",
      text: "End-to-end visibility from sourcing to onboarding to payroll, with clear ownership and accountability.",
    },
    {
      title: "Speed with Quality",
      text: "Tight turnaround times without compromising on skill match, background checks, and candidate experience.",
    },
    {
      title: "PAN-India Presence",
      text: "Ability to support distributed teams, captive centers, and multi-location delivery hubs across India.",
    },
    {
      title: "Partnership Mindset",
      text: "We operate like an extension of your HR & delivery teams, not just as a vendor on the outside.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why should we hire an HR consulting company in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Hiring a specialized HR consulting firm brings external expertise, unbiased auditing, and industry best practices. It allows your internal team to focus on daily operations while we handle strategic transformation and complex compliance issues."
        }
      },
      {
        "@type": "Question",
        "name": "Do you work with startups or only large enterprises?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We work with both. For startups, we build the 'HR Zero-to-One' foundation (policies, hiring, payroll). For enterprises, we focus on optimization, transformation, and scale."
        }
      },
      {
        "@type": "Question",
        "name": "How do you measure the success of an HR consulting project?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We define clear KPIs at the start, such as Reduction in Attrition, Improvement in Employee Net Promoter Score (eNPS), Time-to-Hire efficiency, and Compliance Audit Score improvements."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Minivel different from other HR consultants?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our 'Delivery DNA'. We treat HR projects with the same rigor as IT delivery projects—SLAs, milestones, and data-driven governance. We are accountable for outcomes, not just inputs."
        }
      }
    ]
  };

  return (
    <main className="bg-white text-[#0f172a] font-poppins overflow-hidden">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* ================= HERO ================= */}
      <section className="relative w-full min-h-[65vh] flex items-center px-[7%] py-14 md:py-20 bg-gradient-to-br from-[#0b1230] via-[#131a3d] to-[#050816] text-white">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-10 -top-16 w-[260px] h-[260px] bg-gradient-to-tr from-cyan-400/40 via-sky-500/40 to-indigo-500/40 blur-3xl opacity-70" />
          <div className="absolute -left-16 bottom-[-80px] w-[320px] h-[320px] bg-gradient-to-br from-[#70be44]/35 via-emerald-400/25 to-blue-500/30 blur-3xl opacity-80" />
          <div className="absolute left-1/2 top-1/4 w-[420px] h-[420px] border border-cyan-400/10 rounded-full translate-x-[-50%] opacity-40" />
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          className="relative max-w-3xl z-10"
        >
          <p className="text-[0.875rem] tracking-[0.35em] uppercase text-cyan-300 mb-3">
            Why Minivel
          </p>
          <h1 className="text-[2.4rem] md:text-[3rem] lg:text-[3.4rem] font-extrabold leading-tight">
            A Human-Driven,{" "}
            <span className="bg-gradient-to-r from-[#70be44] via-sky-400 to-fuchsia-500 bg-clip-text text-transparent">
              Technology-Shaped
            </span>{" "}
            Workforce Partner.
          </h1>
          <p className="mt-4 text-sm md:text-base opacity-90 leading-relaxed max-w-2xl">
            Minivel helps enterprises build and manage agile workforces with a
            delivery model inspired by large IT & shared-services organizations
            — predictable, compliant, and built to scale.
          </p>

          {/* TRUST BADGES STRIP (CSS Based) */}
          {/* <div className="mt-8 flex flex-wrap gap-3">
            <span className="px-3 py-1 rounded-full border border-white/20 bg-white/5 text-xs font-medium backdrop-blur-sm flex items-center gap-2">
              <span className="text-[#70be44]">●</span> ISO 9001:2015 Certified
            </span>
            <span className="px-3 py-1 rounded-full border border-white/20 bg-white/5 text-xs font-medium backdrop-blur-sm flex items-center gap-2">
              <span className="text-sky-400">●</span> Startup India Recognized
            </span>
            <span className="px-3 py-1 rounded-full border border-white/20 bg-white/5 text-xs font-medium backdrop-blur-sm flex items-center gap-2">
              <span className="text-fuchsia-400">●</span> GDPR Compliant Process
            </span>
          </div> */}

          {/* Small metrics strip */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-md text-sm">
            <div className="border border-white/15 rounded-xl px-4 py-3 backdrop-blur-sm bg-white/5">
              <p className="text-xl font-semibold">9+ years</p>
              <p className="text-xs opacity-75">Domain experience</p>
            </div>
            <div className="border border-white/15 rounded-xl px-4 py-3 backdrop-blur-sm bg-white/5">
              <p className="text-xl font-semibold">15+ cities</p>
              <p className="text-xs opacity-75">PAN-India reach</p>
            </div>
            <div className="border border-white/15 rounded-xl px-4 py-3 backdrop-blur-sm bg-white/5">
              <p className="text-xl font-semibold">Enterprise</p>
              <p className="text-xs opacity-75">grade processes</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ========== WHY MINIVEL (3D CARD GRID) ========== */}
      <section className="max-w-[1180px] mx-auto mt-[-60px] px-4 pb-10">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {cards.map((card) => (
            <motion.article
              key={card.title}
              variants={fadeUp}
              className="
          group relative bg-white rounded-2xl p-6 
          shadow-[0_8px_22px_rgba(15,23,42,0.06)]
          border border-slate-100
          transition-all duration-400 
          hover:-translate-y-2 hover:-rotate-[0.6deg]
          hover:shadow-[0_18px_45px_rgba(15,23,42,0.16)]
          transform-gpu
        "
            >
              <div className="absolute inset-x-6 -top-1 h-[3px] rounded-full bg-gradient-to-r from-[#70be44] via-sky-400 to-fuchsia-500 opacity-0 group-hover:opacity-100 transform origin-left scale-x-0 group-hover:scale-x-100 transition-all duration-400" />

              <h3 className="text-lg font-semibold mb-2 text-slate-900">
                {card.title}
              </h3>

              <p className="text-[0.95rem] text-slate-600 leading-relaxed">
                {card.text}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* ================= RECOGNIZED BY SECTION ================= */}
      {/* <section className="bg-white py-10 border-y border-slate-100 mb-8">
        <div className="max-w-[1180px] mx-auto px-4 text-center">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-6">
            Recognized & Certified By
          </p>
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
            <span className="text-xl font-bold font-serif text-slate-700">Startup India</span>
            <span className="text-xl font-bold font-sans text-slate-800">ISO 9001:2015</span>
            <span className="text-xl font-bold font-mono text-slate-600">MSME</span>
            <span className="text-xl font-bold font-serif text-slate-700">Make in India</span>
          </div>
        </div>
      </section> */}



      {/* =================== STRATEGIC DELIVERY SECTION =================== */}
      <section className="max-w-[1180px] mx-auto px-4 pb-14 grid md:grid-cols-2 gap-8 items-center">
        <motion.div
          variants={fadeLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="space-y-4"
        >
          <p className="text-[0.875rem] tracking-[0.28em] uppercase text-sky-500">
            Delivery DNA
          </p>
          <h2 className="text-[1.9rem] md:text-[2.5rem] font-extrabold leading-snug bg-gradient-to-r from-gray-800 to-[#70be44] bg-clip-text text-transparent">
            Built like a delivery engine, not just a recruitment partner.
          </h2>
          <p className="text-[0.98rem] text-slate-700 leading-relaxed">
            Our operating model borrows from how large IT and shared-service
            organizations run — structured pods, defined ownership, and layered
            governance to keep hiring & HR operations predictable.
          </p>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-xl p-4 shadow-[0_4px_14px_rgba(15,23,42,0.05)]">
              <p className="font-semibold text-slate-900 mb-1">
                Pod-based teams
              </p>
              <p className="text-xs text-slate-600">
                Dedicated cells aligned to your business units or locations.
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-[0_4px_14px_rgba(15,23,42,0.05)]">
              <p className="font-semibold text-slate-900 mb-1">
                SLA-driven delivery
              </p>
              <p className="text-xs text-slate-600">
                Clear expectations on TAT, quality and reporting cadence.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={fadeRight}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="
            bg-white rounded-2xl p-7 shadow-[0_10px_30px_rgba(15,23,42,0.08)]
            border border-slate-100
          "
        >
          <h3 className="text-lg font-semibold mb-3 text-slate-900">
            How we engage with enterprise clients:
          </h3>
          <ol className="space-y-3 text-[0.95rem] text-slate-700 list-decimal ml-5">
            <li>
              <span className="font-semibold">Discovery & calibration</span> –
              deep dive into roles, locations, SLAs, and stakeholder map.
            </li>
            <li>
              <span className="font-semibold">Dedicated delivery pod</span> –
              recruiters, coordinators, and an account lead aligned to you.
            </li>
            <li>
              <span className="font-semibold">Integrated workflows</span> – JD
              intake, sourcing, screening, interview orchestration, and joining
              management.
            </li>
            <li>
              <span className="font-semibold">Review & governance</span> –
              week-on-week & month-on-month performance reviews with data.
            </li>
          </ol>
        </motion.div>
      </section>
      {/* =================== BOTTOM CTA / SUMMARY =================== */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-[1180px] mx-auto px-4 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <motion.div
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h3 className="text-xl md:text-[1.6rem] font-semibold text-slate-900 mb-2">
              Ready to scale your workforce?
            </h3>
            <p className="text-sm md:text-[0.95rem] text-slate-600 max-w-xl">
              Let’s map your hiring, HR operations, and compliance requirements
              into a single, predictable delivery engine powered by Minivel.
            </p>
          </motion.div>

          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="flex gap-3"
          >
            <button
              onClick={() => (window.location.href = "tel:8956781401")}
              className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#70be44] via-sky-500 to-indigo-500 shadow-[0_10px_30px_rgba(56,189,248,0.35)] hover:shadow-[0_14px_40px_rgba(56,189,248,0.5)] transition-all cursor-pointer"
            >
              Talk to our team
            </button>
            {/* <button className="px-6 py-2.5 rounded-full text-sm font-semibold text-[#0f172a] border border-slate-300 bg-slate-50 hover:bg-slate-100 transition-all">
              Download capability deck
            </button> */}
          </motion.div>
        </div>
      </section>
    </main >
  );
}
